import * as THREE from 'three';
import {
  ARENA_RADIUS, ARENA_WALL_HEIGHT,
  SAND_COLOR, STONE_COLOR, STONE_DARK, COLUMN_COLOR
} from './constants.js';

/* ============================================================
   Enhanced procedural Roman Coliseum with richer detail
   ============================================================ */

export function createArena() {
  const group = new THREE.Group();
  group.name = 'arena';

  // ── Helpers ─────────────────────────────────────
  const stoneMat = (color, rough = 0.85) =>
    new THREE.MeshStandardMaterial({ color, roughness: rough, metalness: 0.05 });

  // ── Arena Floor ────────────────────────────────
  // Multi-ring sand with subtle variation
  const floorGeo = new THREE.CircleGeometry(ARENA_RADIUS, 96);
  const floorMat = new THREE.MeshStandardMaterial({
    color: SAND_COLOR, roughness: 0.95, metalness: 0,
  });
  const floor = new THREE.Mesh(floorGeo, floorMat);
  floor.rotation.x = -Math.PI / 2;
  floor.receiveShadow = true;
  group.add(floor);

  // Concentric ring markings
  const ringRadii = [6, 12, 18, 25, 32];
  for (const r of ringRadii) {
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(r - 0.12, r + 0.12, 80),
      new THREE.MeshStandardMaterial({
        color: 0xb8926a, roughness: 1, transparent: true, opacity: 0.2,
      })
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.015;
    group.add(ring);
  }
  // Cross lines
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const line = new THREE.Mesh(
      new THREE.PlaneGeometry(ARENA_RADIUS * 2, 0.12),
      new THREE.MeshStandardMaterial({
        color: 0xc49a6c, roughness: 1, transparent: true, opacity: 0.18,
      })
    );
    line.rotation.x = -Math.PI / 2;
    line.rotation.z = a;
    line.position.y = 0.012;
    group.add(line);
  }

  // Large center emblem ring
  const emblemOuter = new THREE.Mesh(
    new THREE.RingGeometry(3.8, 4.6, 64),
    new THREE.MeshStandardMaterial({
      color: 0xffffff, roughness: 0.7, transparent: true, opacity: 0.22,
    })
  );
  emblemOuter.rotation.x = -Math.PI / 2;
  emblemOuter.position.y = 0.02;
  group.add(emblemOuter);

  // Inner emblem – a triangle pattern
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 - Math.PI / 2;
    const triGeo = new THREE.ConeGeometry(0.8, 2.2, 3);
    const tri = new THREE.Mesh(triGeo, new THREE.MeshStandardMaterial({
      color: 0xffffff, roughness: 0.8, transparent: true, opacity: 0.15,
    }));
    tri.rotation.x = -Math.PI / 2;
    tri.rotation.z = a;
    tri.position.set(Math.cos(a) * 2, 0.025, Math.sin(a) * 2);
    group.add(tri);
  }

  // ── Scattered debris / props on the sand ───────
  // Small rocks
  for (let i = 0; i < 15; i++) {
    const a = Math.random() * Math.PI * 2;
    const d = 5 + Math.random() * (ARENA_RADIUS - 8);
    const size = 0.15 + Math.random() * 0.35;
    const pebble = new THREE.Mesh(
      new THREE.IcosahedronGeometry(size, 0),
      stoneMat(0x9a8e80, 0.95)
    );
    pebble.position.set(Math.cos(a) * d, size * 0.3, Math.sin(a) * d);
    pebble.rotation.set(Math.random(), Math.random(), Math.random());
    group.add(pebble);
  }

  // Weapon racks (wooden X frames)
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
    const wx = Math.cos(a) * (ARENA_RADIUS - 4);
    const wz = Math.sin(a) * (ARENA_RADIUS - 4);
    const rack = _createWeaponRack();
    rack.position.set(wx, 0, wz);
    rack.rotation.y = -a;
    group.add(rack);
  }

  // ── Arena Wall ────────────────────────────────
  // Lower wall with brick-like detailing
  const wallGeo = new THREE.CylinderGeometry(
    ARENA_RADIUS + 1, ARENA_RADIUS + 1,
    ARENA_WALL_HEIGHT, 80, 3, true
  );
  const wallMat = new THREE.MeshStandardMaterial({
    color: STONE_COLOR, roughness: 0.82, metalness: 0.08, side: THREE.DoubleSide,
  });
  const wall = new THREE.Mesh(wallGeo, wallMat);
  wall.position.y = ARENA_WALL_HEIGHT / 2;
  wall.castShadow = true;
  wall.receiveShadow = true;
  group.add(wall);

  // Horizontal stone course lines on wall
  for (let h = 1; h < ARENA_WALL_HEIGHT; h++) {
    const courseRing = new THREE.Mesh(
      new THREE.TorusGeometry(ARENA_RADIUS + 1.02, 0.06, 4, 80),
      stoneMat(STONE_DARK, 0.9)
    );
    courseRing.rotation.x = Math.PI / 2;
    courseRing.position.y = h;
    group.add(courseRing);
  }

  // Wall top rim — thicker ledge
  const rimGeo = new THREE.TorusGeometry(ARENA_RADIUS + 1, 0.6, 8, 80);
  const rim = new THREE.Mesh(rimGeo, stoneMat(STONE_DARK, 0.7));
  rim.rotation.x = Math.PI / 2;
  rim.position.y = ARENA_WALL_HEIGHT;
  group.add(rim);

  // ── Seating Tiers (stepped) ───────────────────
  const tierCount = 6;
  for (let t = 0; t < tierCount; t++) {
    const r = ARENA_RADIUS + 2 + t * 3;
    const h = ARENA_WALL_HEIGHT + t * 2;
    const tierGeo = new THREE.CylinderGeometry(r + 2.5, r, 2, 48, 1, true);
    const tierMat = new THREE.MeshStandardMaterial({
      color: t % 2 === 0 ? STONE_COLOR : STONE_DARK,
      roughness: 0.85, side: THREE.DoubleSide,
    });
    const tier = new THREE.Mesh(tierGeo, tierMat);
    tier.position.y = h + 1;
    tier.receiveShadow = true;
    group.add(tier);

    // Seat surface
    const seat = new THREE.Mesh(
      new THREE.RingGeometry(r, r + 2.5, 80),
      stoneMat(0x9B8B7A, 0.9)
    );
    seat.rotation.x = -Math.PI / 2;
    seat.position.y = h + 2;
    group.add(seat);

    // Crowd — head + body blobs, denser
    const density = 40 + t * 16;
    for (let c = 0; c < density; c++) {
      const angle = (c / density) * Math.PI * 2 + t * 0.13;
      const dist = r + 0.6 + Math.random() * 1.8;
      const hue = Math.random();
      const bodyH = 0.7 + Math.random() * 0.5;

      // Body
      const body = new THREE.Mesh(
        new THREE.CylinderGeometry(0.22, 0.25, bodyH, 6),
        new THREE.MeshStandardMaterial({
          color: new THREE.Color().setHSL(hue, 0.55, 0.42), roughness: 0.9,
        })
      );
      body.position.set(
        Math.cos(angle) * dist,
        h + 2 + bodyH * 0.5,
        Math.sin(angle) * dist
      );
      group.add(body);

      // Head
      const head = new THREE.Mesh(
        new THREE.SphereGeometry(0.16, 6, 5),
        new THREE.MeshStandardMaterial({ color: 0xFFDBAC, roughness: 0.85 })
      );
      head.position.set(
        Math.cos(angle) * dist,
        h + 2 + bodyH + 0.12,
        Math.sin(angle) * dist
      );
      group.add(head);
    }
  }

  // ── Columns (Corinthian-ish) ──────────────────
  const columnCount = 56;
  const colRing = ARENA_RADIUS + 1.5 + tierCount * 3 + 2;
  const colH = ARENA_WALL_HEIGHT + tierCount * 2 + 7;

  for (let i = 0; i < columnCount; i++) {
    const a = (i / columnCount) * Math.PI * 2;
    const cx = Math.cos(a) * colRing;
    const cz = Math.sin(a) * colRing;

    // Shaft — tapered with entasis
    const shaft = new THREE.Mesh(
      new THREE.CylinderGeometry(0.52, 0.62, colH, 14),
      stoneMat(COLUMN_COLOR, 0.65)
    );
    shaft.position.set(cx, colH / 2, cz);
    shaft.castShadow = true;
    group.add(shaft);

    // Fluting grooves
    for (let f = 0; f < 10; f++) {
      const fa = (f / 10) * Math.PI * 2;
      const flute = new THREE.Mesh(
        new THREE.CylinderGeometry(0.04, 0.04, colH - 1.5, 4),
        stoneMat(0xB8A890, 0.8)
      );
      flute.position.set(cx + Math.cos(fa) * 0.48, colH / 2, cz + Math.sin(fa) * 0.48);
      group.add(flute);
    }

    // Capital (ornamental box + small sphere volutes)
    const capital = new THREE.Mesh(
      new THREE.BoxGeometry(1.5, 0.55, 1.5),
      stoneMat(COLUMN_COLOR, 0.55)
    );
    capital.position.set(cx, colH + 0.28, cz);
    group.add(capital);
    // Volute accents (4 small spheres on corners)
    for (let v = 0; v < 4; v++) {
      const va = (v / 4) * Math.PI * 2 + Math.PI / 4;
      const vol = new THREE.Mesh(
        new THREE.SphereGeometry(0.14, 6, 6),
        stoneMat(0xB8A890, 0.6)
      );
      vol.position.set(
        cx + Math.cos(va) * 0.65,
        colH + 0.45,
        cz + Math.sin(va) * 0.65
      );
      group.add(vol);
    }

    // Plinth base
    const base = new THREE.Mesh(
      new THREE.CylinderGeometry(0.85, 0.9, 0.5, 14),
      stoneMat(STONE_DARK, 0.75)
    );
    base.position.set(cx, 0.25, cz);
    group.add(base);
  }

  // Upper entablature ring
  const entGeo = new THREE.TorusGeometry(colRing, 0.9, 8, 80);
  const ent = new THREE.Mesh(entGeo, stoneMat(COLUMN_COLOR, 0.6));
  ent.rotation.x = Math.PI / 2;
  ent.position.y = colH + 0.9;
  group.add(ent);

  // Second entablature (frieze)
  const friezeGeo = new THREE.TorusGeometry(colRing, 0.5, 6, 80);
  const frieze = new THREE.Mesh(friezeGeo, stoneMat(STONE_DARK, 0.7));
  frieze.rotation.x = Math.PI / 2;
  frieze.position.y = colH + 2.0;
  group.add(frieze);

  // ── 4 Grand Gate arches ──────────────────────
  for (let g = 0; g < 4; g++) {
    const a = (g / 4) * Math.PI * 2;
    const gx = Math.cos(a) * (ARENA_RADIUS + 1);
    const gz = Math.sin(a) * (ARENA_RADIUS + 1);

    // Gate pillars
    for (const side of [-1, 1]) {
      const px = gx + Math.cos(a + Math.PI / 2) * side * 2.2;
      const pz = gz + Math.sin(a + Math.PI / 2) * side * 2.2;
      const pillar = new THREE.Mesh(
        new THREE.BoxGeometry(0.9, ARENA_WALL_HEIGHT + 2, 0.9),
        stoneMat(STONE_DARK, 0.7)
      );
      pillar.position.set(px, (ARENA_WALL_HEIGHT + 2) / 2, pz);
      pillar.castShadow = true;
      group.add(pillar);
    }

    // Lintel
    const lintel = new THREE.Mesh(
      new THREE.BoxGeometry(5.5, 0.8, 1.0),
      stoneMat(STONE_COLOR, 0.7)
    );
    lintel.position.set(gx, ARENA_WALL_HEIGHT + 1.6, gz);
    lintel.rotation.y = a;
    group.add(lintel);

    // Arch top (semicircle)
    const arch = new THREE.Mesh(
      new THREE.TorusGeometry(2.2, 0.35, 8, 20, Math.PI),
      stoneMat(COLUMN_COLOR, 0.6)
    );
    arch.position.set(gx, ARENA_WALL_HEIGHT + 1.6, gz);
    arch.rotation.y = a;
    arch.rotation.x = Math.PI / 2;
    group.add(arch);

    // Dark gap (fake opening)
    const opening = new THREE.Mesh(
      new THREE.PlaneGeometry(3.8, ARENA_WALL_HEIGHT),
      new THREE.MeshBasicMaterial({ color: 0x0a0a0a, side: THREE.DoubleSide })
    );
    opening.position.set(gx, ARENA_WALL_HEIGHT / 2, gz);
    opening.rotation.y = a + Math.PI / 2;
    group.add(opening);
  }

  // ── Statues (4 gladiator silhouettes on wall tops) ──
  for (let s = 0; s < 4; s++) {
    const a = (s / 4) * Math.PI * 2 + Math.PI / 8;
    const sx = Math.cos(a) * (ARENA_RADIUS + 1.5);
    const sz = Math.sin(a) * (ARENA_RADIUS + 1.5);
    const statue = _createStatue();
    statue.position.set(sx, ARENA_WALL_HEIGHT + 0.2, sz);
    statue.rotation.y = -a + Math.PI;
    group.add(statue);
  }

  // ── Braziers / fire bowls (8 around the arena) ──
  for (let b = 0; b < 8; b++) {
    const a = (b / 8) * Math.PI * 2;
    const bx = Math.cos(a) * (ARENA_RADIUS - 2);
    const bz = Math.sin(a) * (ARENA_RADIUS - 2);
    const brazier = _createBrazier();
    brazier.position.set(bx, 0, bz);
    group.add(brazier);
  }

  // ── Banners / flags on alternating columns ──
  for (let i = 0; i < columnCount; i += 4) {
    const a = (i / columnCount) * Math.PI * 2;
    const bx = Math.cos(a) * colRing;
    const bz = Math.sin(a) * colRing;
    const color = i % 8 === 0 ? 0xCC2222 : 0x2244AA;
    const banner = _createBanner(color, colH);
    banner.position.set(bx, 0, bz);
    banner.rotation.y = -a;
    group.add(banner);
  }

  // ── Ground outside arena ──────────────────────
  const outerGround = new THREE.Mesh(
    new THREE.PlaneGeometry(300, 300),
    new THREE.MeshStandardMaterial({ color: 0x3d3028, roughness: 1 })
  );
  outerGround.rotation.x = -Math.PI / 2;
  outerGround.position.y = -0.1;
  outerGround.receiveShadow = true;
  group.add(outerGround);

  return group;
}

// ────────────────────────────────────────────────────────
//  Prop generators
// ────────────────────────────────────────────────────────

function _createWeaponRack() {
  const g = new THREE.Group();
  const wood = new THREE.MeshStandardMaterial({ color: 0x6B4226, roughness: 0.9 });
  // Two crossed poles
  for (const dir of [-1, 1]) {
    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, 3, 6), wood);
    pole.rotation.z = dir * 0.4;
    pole.position.y = 1.5;
    g.add(pole);
  }
  // Horizontal bar
  const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 2, 6), wood);
  bar.rotation.z = Math.PI / 2;
  bar.position.y = 2.0;
  g.add(bar);
  // A prop sword leaning on it
  const blade = new THREE.Mesh(
    new THREE.BoxGeometry(0.06, 1.8, 0.15),
    new THREE.MeshStandardMaterial({ color: 0xAAAAAA, roughness: 0.3, metalness: 0.7 })
  );
  blade.position.set(0.4, 1.2, 0);
  blade.rotation.z = 0.2;
  g.add(blade);
  return g;
}

function _createStatue() {
  const g = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({ color: 0x999080, roughness: 0.7, metalness: 0.1 });
  // Pedestal
  const ped = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.6, 1.2), mat);
  ped.position.y = 0.3;
  g.add(ped);
  // Body
  const body = new THREE.Mesh(new THREE.CylinderGeometry(0.35, 0.4, 2.2, 8), mat);
  body.position.y = 1.7;
  g.add(body);
  // Head
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.3, 8, 8), mat);
  head.position.y = 3.1;
  g.add(head);
  // Arm with sword
  const arm = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.1, 1.4, 6), mat);
  arm.position.set(0.5, 2.2, 0.3);
  arm.rotation.z = -0.6;
  arm.rotation.x = -0.3;
  g.add(arm);
  const sword = new THREE.Mesh(
    new THREE.BoxGeometry(0.05, 1.6, 0.12),
    new THREE.MeshStandardMaterial({ color: 0x888888, roughness: 0.3, metalness: 0.6 })
  );
  sword.position.set(0.9, 3.0, 0.5);
  sword.rotation.z = -0.5;
  g.add(sword);
  return g;
}

function _createBrazier() {
  const g = new THREE.Group();
  const iron = new THREE.MeshStandardMaterial({ color: 0x3a3a3a, roughness: 0.6, metalness: 0.5 });
  // Stand
  const stand = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.3, 1.8, 8), iron);
  stand.position.y = 0.9;
  g.add(stand);
  // Bowl
  const bowl = new THREE.Mesh(new THREE.CylinderGeometry(0.6, 0.35, 0.5, 10, 1, true), iron);
  bowl.position.y = 1.8;
  g.add(bowl);
  // Fire glow (point light)
  const fireLight = new THREE.PointLight(0xFF6600, 2.5, 12);
  fireLight.position.y = 2.3;
  g.add(fireLight);
  // Fire "flame" blobs
  for (let f = 0; f < 5; f++) {
    const flame = new THREE.Mesh(
      new THREE.ConeGeometry(0.12 + Math.random() * 0.1, 0.5 + Math.random() * 0.4, 5),
      new THREE.MeshBasicMaterial({
        color: f < 2 ? 0xFF4400 : 0xFFAA00,
        transparent: true,
        opacity: 0.85,
      })
    );
    flame.position.set(
      (Math.random() - 0.5) * 0.3,
      2.1 + Math.random() * 0.3,
      (Math.random() - 0.5) * 0.3
    );
    g.add(flame);
  }
  return g;
}

function _createBanner(color, columnHeight) {
  const g = new THREE.Group();
  // Pole
  const pole = new THREE.Mesh(
    new THREE.CylinderGeometry(0.04, 0.04, 5, 4),
    new THREE.MeshStandardMaterial({ color: 0x8B4513, roughness: 0.8 })
  );
  pole.position.y = columnHeight + 2;
  g.add(pole);
  // Fabric (flat rectangle hanging)
  const fabric = new THREE.Mesh(
    new THREE.PlaneGeometry(1.2, 3.5),
    new THREE.MeshStandardMaterial({
      color, roughness: 0.8, side: THREE.DoubleSide, transparent: true, opacity: 0.9,
    })
  );
  fabric.position.set(0.7, columnHeight + 1, 0);
  // Slight wave
  const posAttr = fabric.geometry.attributes.position;
  for (let i = 0; i < posAttr.count; i++) {
    const y = posAttr.getY(i);
    posAttr.setZ(i, posAttr.getZ(i) + Math.sin(y * 2) * 0.15);
  }
  fabric.geometry.computeVertexNormals();
  g.add(fabric);
  return g;
}
